import React from 'react';
import type { PublicFiche } from '../../types';

interface CardFooterProps {
  fiche:    PublicFiche;
  compact?: boolean;
}

// ── Abréviations des codes ─────────────────────────────────────────────────────
const CODE_ABBREV: Record<string, string> = {
  'code civil':                                                       'C. civ.',
  'code de commerce':                                                 'C. com.',
  'code de justice administrative':                                   'CJA',
  'code de l\'action sociale et des familles':                        'CASF',
  'code de l\'entrée et du séjour des étrangers et du droit d\'asile': 'CESEDA',
  'code de l\'environnement':                                         'C. env.',
  'code de l\'expropriation pour cause d\'utilité publique':          'C. expro.',
  'code de l\'organisation judiciaire':                               'COJ',
  'code de l\'urbanisme':                                             'C. urb.',
  'code de la commande publique':                                     'CCP',
  'code de la consommation':                                          'C. conso.',
  'code de la construction et de l\'habitation':                      'CCH',
  'code de la justice pénale des mineurs':                            'CJPM',
  'code de la propriété intellectuelle':                              'CPI',
  'code de la route':                                                 'C. route',
  'code de la sécurité intérieure':                                   'CSI',
  'code de la sécurité sociale':                                      'CSS',
  'code de procédure civile':                                         'CPC',
  'code de procédure pénale':                                         'CPP',
  'code des assurances':                                              'C. assur.',
  'code des communes':                                                'C. com.',
  'code des douanes':                                                 'C. douanes',
  'code des postes et des communications électroniques':              'CPCE',
  'code des procédures civiles d\'exécution':                         'CPCE',
  'code des relations entre le public et l\'administration':          'CRPA',
  'code du travail':                                                  'C. trav.',
  'code général de la fonction publique':                             'CGFP',
  'code général de la propriété des personnes publiques':             'CG3P',
  'code général des collectivités territoriales':                     'CGCT',
  'code général des impôts':                                          'CGI',
  'code général des impots':                                          'CGI',
  'code monétaire et financier':                                      'CMF',
  'code pénal':                                                       'C. pén.',
  'code rural':                                                       'C. rural',
  'code rural et de la pêche maritime':                               'C. rural',
  'loi':                                                              'Loi',
};

function abbrevCode(code: string): string {
  // Normalise les apostrophes courbes → droites, et les espaces insécables
  const key = code.toLowerCase().trim()
    .replace(/[‘’ʼ]/g, "'")
    .replace(/\u00a0/g, ' ');
  if (CODE_ABBREV[key]) return CODE_ABBREV[key];
  // Fallback : cherche un préfixe connu (CESEDA, code du travail partiel…)
  const found = Object.keys(CODE_ABBREV).find(k => key.startsWith(k.slice(0, 12)));
  if (found) return CODE_ABBREV[found];
  // Dernier recours : retire "code " et prend les mots significatifs
  const words = code.replace(/^code\s+/i, '').split(/\s+/).filter(w => !['de', 'du', 'des', 'la', 'le', 'les', 'et', 'l\'', "l'"].includes(w.toLowerCase()));
  return ('C. ' + words.slice(0, 2).join(' ')).slice(0, 14);
}

function parseArticle(art: unknown): { abbrev: string; article: string; full: string } {
  if (typeof art === 'string') {
    // Format hérité "Code du travail L.1221-1"
    const match = art.match(/^((?:code|loi)[^0-9L]*?)\s+((?:art\.\s*)?[LRA]?\d.*)$/i);
    if (match) {
      return { abbrev: abbrevCode(match[1].trim()), article: match[2].replace(/^art\.\s*/i, ''), full: art };
    }
    return { abbrev: 'CODE', article: art, full: art };
  }
  const a = art as any;
  const codeFull  = (a.code    ?? '').trim();
  const articleNum = (a.article ?? '').trim().replace(/^art\.\s*/i, '');
  return {
    abbrev: abbrevCode(codeFull),
    article: articleNum,
    full: `${codeFull} art. ${articleNum}`,
  };
}

const ArticleBadge = ({ art, compact }: { art: unknown; compact: boolean }) => {
  const { abbrev, article, full } = parseArticle(art);
  return (
    <span
      className={`inline-flex items-center gap-1 bg-background border border-sl-blue-m rounded-md text-[10px] text-foreground/80 font-medium ${compact ? 'px-1.5 py-0.5' : 'px-2 py-0.5'}`}
      title={full}
    >
      <span className="text-[8px] font-bold text-muted-foreground uppercase tracking-wide border-r border-border pr-1 mr-0.5 flex-shrink-0 whitespace-nowrap">
        {abbrev}
      </span>
      <span className={`font-mono ${compact ? 'truncate max-w-[70px]' : ''}`}>
        {article ? `art. ${article}` : full}
      </span>
    </span>
  );
};

export const CardFooter = ({ fiche, compact = false }: CardFooterProps) => {
  const articles = fiche.articles ?? [];
  const limit    = compact ? 3 : 4;

  if (!compact) {
    return (
      <div className="px-5 pt-3 pb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap pt-2.5 border-t border-border/60">
          <div className="flex items-center gap-1.5 flex-wrap">
            {articles.slice(0, limit).map((art, i) => (
              <ArticleBadge key={i} art={art} compact={false} />
            ))}
            {articles.length > limit && (
              <span className="text-[10px] text-muted-foreground">+{articles.length - limit}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 justify-end">
            {fiche.domaine && (
              <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2.5 py-0.5 rounded-full font-medium">
                {fiche.domaine}
              </span>
            )}
            {(Array.isArray(fiche.concepts) ? fiche.concepts : []).slice(0, 2).map((c, i) => (
              <span key={i} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{c}</span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 pt-2.5 pb-4 border-t border-border/60 mt-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {articles.slice(0, limit).map((art, i) => (
            <ArticleBadge key={i} art={art} compact={true} />
          ))}
          {articles.length > limit && (
            <span className="text-[10px] text-muted-foreground">+{articles.length - limit}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {fiche.domaine && (
            <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded-full font-medium">
              {fiche.domaine}
            </span>
          )}
          {(Array.isArray(fiche.concepts) ? fiche.concepts : []).slice(0, 1).map((c, i) => (
            <span key={i} className="text-[10px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{c}</span>
          ))}
        </div>
      </div>
    </div>
  );
};
